import { ToolItem } from '../types';

export interface DevToolItem extends ToolItem {
  devCategory: 'json-jwt' | 'crypto-hash' | 'regex-time' | 'code-format' | 'css-svg' | 'web-http';
  devCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const DEV_CATEGORIES_TABS = [
  { id: 'all', label: 'All Developer Tools', icon: 'Code2' },
  { id: 'json-jwt', label: 'JSON & JWT', icon: 'Braces' },
  { id: 'crypto-hash', label: 'Crypto & Identifiers', icon: 'ShieldCheck' },
  { id: 'regex-time', label: 'Regex, Cron & Time', icon: 'Clock' },
  { id: 'code-format', label: 'Code Format & Minify', icon: 'FileCode' },
  { id: 'css-svg', label: 'CSS & SVG Tools', icon: 'Palette' },
  { id: 'web-http', label: 'Web & Browser Info', icon: 'Globe' }
];

export const DEV_TOOLS_DATA: DevToolItem[] = [
  // --- 1. JSON & JWT (13 Tools) ---
  {
    id: 'json-formatter',
    name: 'JSON Formatter',
    description: 'Format, prettify, and clean raw JSON data with customizable tab indentation.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Braces',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'json-validator',
    name: 'JSON Validator',
    description: 'Validate JSON syntax structure and pinpoint line-by-line syntax error positions.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'CheckCircle2',
    actionType: 'generic'
  },
  {
    id: 'json-compare',
    name: 'JSON Compare',
    description: 'Compare two JSON objects side-by-side to highlight added, removed, or modified keys.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'GitCompare',
    actionType: 'generic'
  },
  {
    id: 'json-diff',
    name: 'JSON Diff',
    description: 'Compute precise structural diff patches between two complex JSON payloads.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Columns',
    actionType: 'generic'
  },
  {
    id: 'jwt-decoder',
    name: 'JWT Decoder',
    description: 'Decode JSON Web Token headers, payload claims, and signature algorithms safely in browser.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Key',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'jwt-viewer',
    name: 'JWT Viewer',
    description: 'Inspect token expiration times (exp), issuer details, and custom authentication claims.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Eye',
    actionType: 'generic'
  },
  {
    id: 'jwt-inspector',
    name: 'JWT Inspector',
    description: 'Analyze security settings, algorithm strength, and claims timeline of JWT tokens.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'ShieldCheck',
    actionType: 'generic'
  },
  {
    id: 'jwt-generator',
    name: 'JWT Generator',
    description: 'Generate custom signed test JWT tokens with HMAC SHA256 secrets for debugging APIs.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Lock',
    actionType: 'generic'
  },
  {
    id: 'csv-to-json',
    name: 'CSV to JSON',
    description: 'Convert CSV spreadsheet tables into structured array of JSON objects.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Table',
    actionType: 'generic'
  },
  {
    id: 'json-to-csv',
    name: 'JSON to CSV',
    description: 'Convert JSON object arrays into downloadable CSV or TSV spreadsheet files.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'FileSpreadsheet',
    actionType: 'generic'
  },
  {
    id: 'json-to-yaml',
    name: 'JSON to YAML',
    description: 'Convert JSON data structures into clean YAML configuration format.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'FileCode',
    actionType: 'generic'
  },
  {
    id: 'yaml-to-json',
    name: 'YAML to JSON',
    description: 'Parse YAML configurations into standardized JSON format.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'json-tree-viewer',
    name: 'JSON Tree Viewer',
    description: 'Interactive collapsible tree view node inspector for nested JSON data.',
    devCategory: 'json-jwt',
    devCategoryLabel: 'JSON & JWT',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Network',
    actionType: 'generic'
  },

  // --- 2. CRYPTO & IDENTIFIERS (13 Tools) ---
  {
    id: 'uuid-generator',
    name: 'UUID Generator',
    description: 'Generate bulk cryptographically secure v4 UUIDs (Universally Unique Identifiers).',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Dices',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'nanoid-generator',
    name: 'Nano ID Generator',
    description: 'Generate compact, URL-friendly, unique string IDs with custom alphabet parameters.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  },
  {
    id: 'hash-generator',
    name: 'Hash Generator',
    description: 'Generate cryptographic hashes simultaneously across MD5, SHA1, SHA256, and SHA512.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Hash',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'md5-hash',
    name: 'MD5 Generator',
    description: 'Compute 128-bit MD5 checksum hashes for strings or data verification.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Shield',
    actionType: 'generic'
  },
  {
    id: 'sha1-hash',
    name: 'SHA1 Generator',
    description: 'Calculate 160-bit SHA-1 hash strings for legacy data integrity checking.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Lock',
    actionType: 'generic'
  },
  {
    id: 'sha256-hash',
    name: 'SHA256 Generator',
    description: 'Generate secure 256-bit SHA-2 hashes for authentication and digital signatures.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'ShieldCheck',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'sha512-hash',
    name: 'SHA512 Generator',
    description: 'Compute high-security 512-bit SHA-2 cryptographic hash digests.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Key',
    actionType: 'generic'
  },
  {
    id: 'hmac-generator',
    name: 'HMAC Generator',
    description: 'Compute Keyed-Hash Message Authentication Codes using SHA256 or SHA512 keys.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Cpu',
    actionType: 'generic'
  },
  {
    id: 'bcrypt-generator',
    name: 'Bcrypt Generator',
    description: 'Generate salted Bcrypt password hashes with configurable work factor cost rounds.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Lock',
    actionType: 'generic'
  },
  {
    id: 'base64-dev',
    name: 'Base64 Tool',
    description: 'Encode and decode UTF-8 text or binary files into Base64 format.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Binary',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'url-encode-dev',
    name: 'URL Encode / Decode',
    description: 'Convert characters to percent-encoded URI strings and back.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Globe',
    actionType: 'generic'
  },
  {
    id: 'html-encode-dev',
    name: 'HTML Encode / Decode',
    description: 'Escape special HTML entities like &lt;, &gt;, and &amp; for secure template rendering.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'escape-characters',
    name: 'Escape Characters',
    description: 'Escape or unescape JavaScript, JSON, C#, or SQL string backslashes and quotes.',
    devCategory: 'crypto-hash',
    devCategoryLabel: 'Crypto & Identifiers',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Terminal',
    actionType: 'generic'
  },

  // --- 3. REGEX, CRON & TIME (8 Tools) ---
  {
    id: 'regex-tester',
    name: 'Regex Tester',
    description: 'Test and debug Regular Expressions in real-time with regex match highlights and flags.',
    devCategory: 'regex-time',
    devCategoryLabel: 'Regex, Cron & Time',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Terminal',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'regex-cheat-sheet',
    name: 'Regex Cheat Sheet',
    description: 'Quick reference guide for regex syntax, character classes, lookaheads, and quantifiers.',
    devCategory: 'regex-time',
    devCategoryLabel: 'Regex, Cron & Time',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'FileText',
    actionType: 'generic'
  },
  {
    id: 'cron-generator',
    name: 'Cron Generator',
    description: 'Build valid 5-part or 6-part cron schedule expressions visually.',
    devCategory: 'regex-time',
    devCategoryLabel: 'Regex, Cron & Time',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Clock',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'cron-parser',
    name: 'Cron Parser',
    description: 'Parse cron syntax into human-readable schedule explanations and upcoming trigger dates.',
    devCategory: 'regex-time',
    devCategoryLabel: 'Regex, Cron & Time',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Calendar',
    actionType: 'generic'
  },
  {
    id: 'unix-timestamp-converter',
    name: 'Unix Timestamp Converter',
    description: 'Convert Unix epoch seconds/milliseconds to human-readable ISO date strings.',
    devCategory: 'regex-time',
    devCategoryLabel: 'Regex, Cron & Time',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'RefreshCw',
    actionType: 'generic'
  },
  {
    id: 'epoch-converter',
    name: 'Epoch Converter',
    description: 'Get current epoch timestamp in seconds/ms and convert timestamps across formats.',
    devCategory: 'regex-time',
    devCategoryLabel: 'Regex, Cron & Time',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Timer',
    actionType: 'generic'
  },
  {
    id: 'date-formatter',
    name: 'Date Formatter',
    description: 'Format dates into ISO 8601, RFC 2822, custom locale strings, and UTC offsets.',
    devCategory: 'regex-time',
    devCategoryLabel: 'Regex, Cron & Time',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'CalendarDays',
    actionType: 'generic'
  },
  {
    id: 'timezone-converter',
    name: 'Timezone Converter',
    description: 'Convert timestamps across global time zones (UTC, EST, PST, GMT, CET, IST).',
    devCategory: 'regex-time',
    devCategoryLabel: 'Regex, Cron & Time',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Globe',
    actionType: 'generic'
  },

  // --- 4. CODE FORMAT & MINIFY (9 Tools) ---
  {
    id: 'css-minifier',
    name: 'CSS Minifier',
    description: 'Compress and strip comments/whitespace from CSS stylesheets to optimize load times.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Minimize2',
    actionType: 'generic'
  },
  {
    id: 'css-beautifier',
    name: 'CSS Beautifier',
    description: 'Format and prettify minified CSS code into structured, indented rulesets.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  },
  {
    id: 'js-minifier',
    name: 'JS Minifier',
    description: 'Minify JavaScript code blocks, removing comments and unneeded spaces.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'FileCode',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'js-beautifier',
    name: 'JS Beautifier',
    description: 'Format and unminify compressed JavaScript or TypeScript code.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'html-minifier',
    name: 'HTML Minifier',
    description: 'Compress HTML documents by removing redundant line breaks and spaces.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Layers',
    actionType: 'generic'
  },
  {
    id: 'html-beautifier',
    name: 'HTML Beautifier',
    description: 'Indent and format raw HTML documents with proper DOM tag alignment.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'AlignLeft',
    actionType: 'generic'
  },
  {
    id: 'sql-formatter',
    name: 'SQL Formatter',
    description: 'Format SQL queries with capitalized keywords (SELECT, FROM, WHERE) and indentation.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Database',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'xml-formatter',
    name: 'XML Formatter',
    description: 'Pretty-print and validate XML document tags with tree hierarchy views.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Code2',
    actionType: 'generic'
  },
  {
    id: 'yaml-formatter',
    name: 'YAML Formatter',
    description: 'Validate and format YAML configuration files with proper indentation checking.',
    devCategory: 'code-format',
    devCategoryLabel: 'Code Format & Minify',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'SlidersHorizontal',
    actionType: 'generic'
  },

  // --- 5. CSS & SVG GENERATORS (14 Tools) ---
  {
    id: 'color-converter',
    name: 'Color Converter',
    description: 'Convert color codes between HEX, RGB, HSL, HSV, and CMYK formats.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Palette',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'hex-rgb',
    name: 'HEX ↔ RGB',
    description: 'Instant conversion between Hexadecimal color strings (#3b82f6) and RGB values.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'SlidersHorizontal',
    actionType: 'generic'
  },
  {
    id: 'hex-hsl',
    name: 'HEX ↔ HSL',
    description: 'Convert Hex colors to Hue, Saturation, Lightness percentage parameters.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'SlidersHorizontal',
    actionType: 'generic'
  },
  {
    id: 'hex-cmyk',
    name: 'HEX ↔ CMYK',
    description: 'Translate web Hex colors into print-ready CMYK ink percentage values.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Printer',
    actionType: 'generic'
  },
  {
    id: 'css-gradient-generator',
    name: 'CSS Gradient Generator',
    description: 'Create multi-stop linear and radial CSS gradients with copyable CSS code.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Sparkles',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'css-clip-path-generator',
    name: 'CSS Clip Path Generator',
    description: 'Design custom polygon, circle, and ellipse CSS clip-path shapes visually.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Scissors',
    actionType: 'generic'
  },
  {
    id: 'css-box-shadow-generator',
    name: 'CSS Box Shadow Generator',
    description: 'Build layered, smooth CSS box-shadow styles with live element preview.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Layers',
    actionType: 'generic'
  },
  {
    id: 'css-border-radius-generator',
    name: 'CSS Border Radius Generator',
    description: 'Custom organic border-radius generator for blob shapes and curved buttons.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Circle',
    actionType: 'generic'
  },
  {
    id: 'css-flexbox-playground',
    name: 'CSS Flexbox Playground',
    description: 'Interactive visual playground to test flex-direction, align-items, and justify-content.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'LayoutGrid',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'css-grid-playground',
    name: 'CSS Grid Playground',
    description: 'Build responsive 2D CSS grid layouts visually and export CSS grid code.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Table',
    actionType: 'generic'
  },
  {
    id: 'svg-path-generator',
    name: 'SVG Path Generator',
    description: 'Generate vector SVG paths, waves, curves, and divider shapes.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'PenTool',
    actionType: 'generic'
  },
  {
    id: 'svg-optimizer',
    name: 'SVG Optimizer',
    description: 'Clean SVG markup, remove unused attributes, and shrink vector file size.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Zap',
    actionType: 'generic'
  },
  {
    id: 'svg-viewer',
    name: 'SVG Viewer',
    description: 'Inspect SVG vector graphics markup, preview rendering, and check viewBox scale.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Eye',
    actionType: 'generic'
  },
  {
    id: 'svg-minifier',
    name: 'SVG Minifier',
    description: 'Minify SVG vector XML code into compact single-line vector strings.',
    devCategory: 'css-svg',
    devCategoryLabel: 'CSS & SVG Tools',
    iconBgColor: 'bg-slate-500/15',
    iconTextColor: 'text-slate-400',
    iconName: 'Minimize2',
    actionType: 'generic'
  },

  // --- 6. WEB, HTTP & BROWSER INFO (10 Tools) ---
  {
    id: 'http-status-lookup',
    name: 'HTTP Status Lookup',
    description: 'Search HTTP response status codes (200, 301, 404, 500) with detailed RFC specs.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Globe',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'mime-type-lookup',
    name: 'MIME Type Lookup',
    description: 'Look up Content-Type MIME header strings by file extension or file category.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'FileText',
    actionType: 'generic'
  },
  {
    id: 'http-header-viewer',
    name: 'HTTP Header Viewer',
    description: 'Inspect standard request and response HTTP headers and security policy flags.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Server',
    actionType: 'generic'
  },
  {
    id: 'user-agent-parser',
    name: 'User Agent Parser',
    description: 'Parse browser User-Agent strings to extract browser version, OS, and device type.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Cpu',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'device-info',
    name: 'Device Info',
    description: 'Inspect current client screen resolution, pixel ratio, platform, and hardware details.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Smartphone',
    actionType: 'generic'
  },
  {
    id: 'screen-resolution-checker',
    name: 'Screen Resolution Checker',
    description: 'Check screen width, height, color depth, and device pixel ratio in real-time.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Monitor',
    actionType: 'generic'
  },
  {
    id: 'viewport-checker',
    name: 'Viewport Checker',
    description: 'Measure current window inner width/height and responsive breakpoint triggers.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Maximize2',
    actionType: 'generic'
  },
  {
    id: 'browser-info',
    name: 'Browser Info',
    description: 'Check cookie support, LocalStorage availability, language locale, and WebGL state.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'ExternalLink',
    actionType: 'generic'
  },
  {
    id: 'local-storage-viewer',
    name: 'Local Storage Viewer',
    description: 'Inspect, edit, clear, and debug browser localStorage key-value pairs.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Database',
    actionType: 'generic'
  },
  {
    id: 'cookie-viewer',
    name: 'Cookie Viewer',
    description: 'Inspect active browser document cookies, domain paths, and expiration parameters.',
    devCategory: 'web-http',
    devCategoryLabel: 'Web & Browser Info',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'ShieldCheck',
    actionType: 'generic'
  }
];
